import React, { useCallback, useEffect, useInsertionEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Avatar from '../Avatar';
import CaretDownIcon from '../CustomIcons/CaretDownIcon';
import CaretRightIcon from '../CustomIcons/CaretRightIcon';
import CaretUpIcon from '../CustomIcons/CaretUpIcon';
import CheckIcon from '../CustomIcons/CheckIcon';
import PillButton from '../PillButton';
import { pushEscapeLayer } from '../escapeLayers';
import { ensureOverlayStyles, overlayIdentity } from '../portalOverlay';
import { useTopLayer } from '../topLayer';

export interface ChatModelSubOption {
    value: string;
    label: string;
    description?: string;
}

export interface ChatModelOption {
    value: string;
    label: string;
    /** The provider's mark. A URL, since providers ship logos rather than icon fonts. */
    iconSrc?: string;
    /** A second choice the model offers, e.g. how long it may think. */
    options?: ChatModelSubOption[];
}

export interface ChatModelPickerProps {
    models: ChatModelOption[];
    value: string;
    /** The chosen sub-option, when the current model offers them. */
    optionValue?: string;
    /** Called with the model, and its sub-option when it has them. */
    onChange: (model: string, option?: string) => void;
    disabled?: boolean;
    /**
     * Opens upward. On by default: this sits under a composer at the bottom of a column, so
     * downward is almost always off-screen.
     */
    openUp?: boolean;
    'aria-label'?: string;
}

const VIEWPORT_PADDING = 8;
const PANEL_MIN_WIDTH = 220;
const SUBMENU_MIN_WIDTH = 240;

/**
 * Inline rather than Tailwind: the panel is portalled to `document.body`, which the scoped
 * shadow stylesheet does not reach. Same reason `ActionMenu` styles its popover inline.
 */
const panelStyle = (
    position: { top: number; left: number } | null,
    minWidth: number,
): React.CSSProperties => ({
    position: 'fixed',
    top: position?.top ?? 0,
    left: position?.left ?? 0,
    minWidth,
    background: 'var(--malachite-card-background, rgb(255, 255, 255))',
    border: '1px solid var(--malachite-card-border, rgb(232, 232, 232))',
    borderRadius: 12,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
    padding: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    visibility: position ? 'visible' : 'hidden',
    opacity: position ? 1 : 0,
});

const ROW_STYLE: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    textAlign: 'left',
    width: '100%',
    cursor: 'pointer',
    fontSize: 'var(--malachite-font-size-small, 13px)',
    color: 'var(--malachite-text-primary, rgb(10, 10, 10))',
};

const DESCRIPTION_STYLE: React.CSSProperties = {
    fontSize: 'var(--malachite-font-size-xsmall, 11px)',
    color: 'var(--malachite-text-dimmed, rgb(161, 161, 161))',
};

const CHECK_STYLE: React.CSSProperties = {
    marginLeft: 'auto',
    display: 'inline-flex',
    color: 'var(--malachite-text-success, rgb(2, 122, 66))',
};

/**
 * Which model answers, and how hard it may think.
 *
 * Its own component rather than a `Dropdown`: that one is a form select bound to
 * react-hook-form, and this is a menu whose rows open a second menu. Portalled for the same
 * reason `ActionMenu` is — a panel inside the composer would be clipped by it.
 */
const ChatModelPicker: React.FC<ChatModelPickerProps> = ({
    models,
    value,
    optionValue,
    onChange,
    disabled = false,
    openUp = true,
    'aria-label': ariaLabel = 'Model',
}) => {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
    const [submenuFor, setSubmenuFor] = useState<string | null>(null);
    const [submenuPosition, setSubmenuPosition] = useState<{ top: number; left: number } | null>(
        null,
    );
    const triggerRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const submenuRef = useRef<HTMLDivElement>(null);

    useTopLayer(panelRef, open);

    const selected = models.find((model) => model.value === value);

    const place = useCallback(() => {
        const trigger = triggerRef.current;
        const panel = panelRef.current;

        if (!trigger) {
            return;
        }

        const rect = trigger.getBoundingClientRect();
        const width = panel?.offsetWidth || PANEL_MIN_WIDTH;
        const height = panel?.offsetHeight || 0;
        const maxLeft = window.innerWidth - width - VIEWPORT_PADDING;
        const left = Math.max(VIEWPORT_PADDING, Math.min(rect.right - width, maxLeft));
        // Flips when the chosen side has no room, so the panel is never off-screen.
        const above = rect.top - height - 4;
        const below = rect.bottom + 4;
        const fitsAbove = above >= VIEWPORT_PADDING;
        const fitsBelow = height === 0 || below + height <= window.innerHeight - VIEWPORT_PADDING;
        const top = openUp ? (fitsAbove ? above : below) : fitsBelow ? below : above;

        setPosition({ top, left });
    }, [openUp]);

    useEffect(() => {
        if (!open) {
            setSubmenuFor(null);
            setSubmenuPosition(null);
            return;
        }

        place();

        const reposition = () => place();
        const closeOnOutside = (event: MouseEvent) => {
            // `composedPath`, not `event.target`: this listener is on `document`, and the
            // trigger lives in a shadow root, so the target is retargeted to the shadow HOST.
            // `contains` then says the trigger was not clicked, the menu closes on mousedown,
            // and the click that follows toggles it straight back open.
            const path = event.composedPath();
            const insideOwnUi = [triggerRef.current, panelRef.current, submenuRef.current].some(
                (node) => node !== null && path.includes(node),
            );

            if (!insideOwnUi) {
                setOpen(false);
            }
        };
        const popEscapeLayer = pushEscapeLayer(() => setOpen(false));

        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        document.addEventListener('mousedown', closeOnOutside, true);

        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
            document.removeEventListener('mousedown', closeOnOutside, true);
            popEscapeLayer();
        };
    }, [open, place]);

    useInsertionEffect(() => {
        if (open) {
            ensureOverlayStyles();
        }
    }, [open]);

    const openSubmenu = (model: ChatModelOption, row: HTMLElement) => {
        if (!model.options?.length) {
            setSubmenuFor(null);
            return;
        }

        const rect = row.getBoundingClientRect();
        const panelLeft = panelRef.current?.getBoundingClientRect().left ?? rect.left;
        // To the left of the parent panel by default, flipping right when that would clip.
        const toLeft = panelLeft - SUBMENU_MIN_WIDTH - 4;
        const left =
            toLeft >= VIEWPORT_PADDING
                ? toLeft
                : Math.min(
                      (panelRef.current?.getBoundingClientRect().right ?? rect.right) + 4,
                      window.innerWidth - SUBMENU_MIN_WIDTH - VIEWPORT_PADDING,
                  );

        setSubmenuFor(model.value);
        setSubmenuPosition({ top: rect.top, left });
    };

    const choose = (model: ChatModelOption, option?: ChatModelSubOption) => {
        onChange(model.value, option?.value);
        setOpen(false);
    };

    const submenuModel = models.find((model) => model.value === submenuFor);

    return (
        <>
            <div ref={triggerRef} className="inline-flex">
                <PillButton
                    label={selected?.label ?? value}
                    icon={
                        selected?.iconSrc ? (
                            <Avatar src={selected.iconSrc} name={selected.label} size={14} />
                        ) : undefined
                    }
                    trailingIcon={
                        openUp ? (
                            <CaretUpIcon size={10} />
                        ) : (
                            <CaretDownIcon size={10} />
                        )
                    }
                    shape="tab"
                    size="small"
                    disabled={disabled}
                    onClick={() => setOpen((wasOpen) => !wasOpen)}
                />
            </div>
            {open &&
                createPortal(
                    <>
                        <div
                            ref={panelRef}
                            role="menu"
                            aria-label={ariaLabel}
                            // `actionmenu` rather than a new kind: the identity is a
                            // host-facing allow-list, so a new class would need every host to
                            // update its CSS, and this panel is an action menu in shape.
                            {...overlayIdentity('actionmenu')}
                            style={panelStyle(position, PANEL_MIN_WIDTH)}
                        >
                            {models.map((model) => (
                                <button
                                    key={model.value}
                                    type="button"
                                    role="menuitem"
                                    aria-haspopup={model.options?.length ? 'menu' : undefined}
                                    style={ROW_STYLE}
                                    onMouseEnter={(event) =>
                                        openSubmenu(model, event.currentTarget)
                                    }
                                    onFocus={(event) => openSubmenu(model, event.currentTarget)}
                                    onClick={() =>
                                        model.options?.length
                                            ? choose(model, model.options[0])
                                            : choose(model)
                                    }
                                >
                                    {model.iconSrc ? (
                                        <Avatar src={model.iconSrc} name={model.label} size={14} />
                                    ) : null}
                                    {model.label}
                                    {model.value === value ? (
                                        <span style={CHECK_STYLE}>
                                            <CheckIcon size={11} />
                                        </span>
                                    ) : null}
                                    {model.options?.length ? (
                                        <span
                                            style={{
                                                marginLeft: model.value === value ? 4 : 'auto',
                                                display: 'inline-flex',
                                                color: 'var(--malachite-text-dimmed, rgb(161, 161, 161))',
                                            }}
                                        >
                                            <CaretRightIcon size={10} />
                                        </span>
                                    ) : null}
                                </button>
                            ))}
                        </div>
                        {submenuModel?.options?.length ? (
                            <div
                                ref={submenuRef}
                                role="menu"
                                aria-label={`${submenuModel.label} options`}
                                {...overlayIdentity('actionmenu')}
                                style={panelStyle(submenuPosition, SUBMENU_MIN_WIDTH)}
                            >
                                {submenuModel.options.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        role="menuitem"
                                        style={{ ...ROW_STYLE, alignItems: 'flex-start' }}
                                        onClick={() => choose(submenuModel, option)}
                                    >
                                        <span
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 2,
                                            }}
                                        >
                                            {option.label}
                                            {option.description ? (
                                                <span style={DESCRIPTION_STYLE}>
                                                    {option.description}
                                                </span>
                                            ) : null}
                                        </span>
                                        {submenuModel.value === value &&
                                        option.value === optionValue ? (
                                            <span style={CHECK_STYLE}>
                                                <CheckIcon size={11} />
                                            </span>
                                        ) : null}
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </>,
                    document.body,
                )}
        </>
    );
};

export default ChatModelPicker;
export { ChatModelPicker };
